// src/components/AuthBridge.js
import { useEffect, useRef } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

// 🔒 Origem do site PAI (Weebly). Use SÓ o domínio (sem path).
const PARENT_ORIGIN =
  process.env.NODE_ENV === "production"
    ? "https://www.termometrooscar.com"
    : "*"; // em dev pode usar "*", depois publique com o domínio acima

/**
 * Monta o payload a ser enviado ao site pai.
 * - Se não houver user OU se o e-mail não estiver verificado, retorna null
 *   (assim a barra externa permanece em "ENTRAR | REGISTRAR").
 */
async function buildPayload(user) {
  if (!user) return null;
  if (!user.emailVerified) return null; // 🔒 NÃO expor estado "logado" sem verificação

  let nome = user.displayName || "";
  let foto = user.photoURL || "";

  try {
    const snap = await getDoc(doc(db, "usuarios", user.uid));
    if (snap.exists()) {
      const d = snap.data();
      if (d?.nome || d?.sobrenome) {
        nome = `${d?.nome || ""} ${d?.sobrenome || ""}`.trim();
      }
      if (d?.fotoPerfil) {
        foto = d.fotoPerfil;
      }
    }
  } catch {
    // silencioso
  }

  // Fallback acordado quando não houver foto válida
  const FOTO_DEFAULT = "https://i.imgur.com/aJOmnzh.png";

  const fotoFinal = (() => {
    const p = (foto || "").trim();
    if (!p || p.toLowerCase() === "null" || p.toLowerCase() === "undefined") return FOTO_DEFAULT;
    try {
      const u = new URL(p);
      const hasExt = /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(u.pathname);
      const host = u.hostname.toLowerCase();
      const trusted = /googleusercontent\.com$|firebasestorage\.googleapis\.com$|^i\.imgur\.com$/.test(host);
      return (hasExt || trusted) ? p : FOTO_DEFAULT;
    } catch {
      return FOTO_DEFAULT;
    }
  })();

  return {
    uid: user.uid,
    name: (nome || "Utilizador").trim(),
    photo: fotoFinal,
    verified: true, // opcional: indica explicitamente que está verificado
  };
}

export default function AuthBridge() {
  const lastUserRef = useRef(null);

  // Sempre que o estado de auth mudar, avisamos o "pai"
  useEffect(() => {
    if (window === window.parent) return; // não está em iframe

    const unsub = onAuthStateChanged(auth, async (user) => {
      // só envia payload se verificado; senão, envia null
      let payload = await buildPayload(user);
      lastUserRef.current = payload ?? null;

      try {
        window.parent.postMessage({ type: "TM_AUTH", user: payload }, PARENT_ORIGIN);
      } catch {
        // silencioso
      }
    });

    return () => unsub();
  }, []);

  // Recebe comandos do "pai"
  useEffect(() => {
    if (window === window.parent) return;

    async function onMessage(e) {
      if (PARENT_ORIGIN !== "*" && e.origin !== PARENT_ORIGIN) return;
      const data = e.data || {};

      if (data.type === "TM_WHATS_YOUR_AUTH") {
        // Responde com o cache; se não houver, tenta currentUser (mas só se verificado)
        const payload =
          lastUserRef.current ??
          (auth.currentUser ? await buildPayload(auth.currentUser) : null);

        lastUserRef.current = payload ?? null;

        try {
          window.parent.postMessage({ type: "TM_AUTH", user: payload }, PARENT_ORIGIN);
        } catch {
          // silencioso
        }
      }

      if (data.type === "TM_LOGOUT") {
        try {
          await signOut(auth);
        } catch {
          // silencioso
        }
        // Notifica imediatamente o pai para evitar UI presa no usuário antigo
        lastUserRef.current = null;
        try {
          window.parent.postMessage({ type: "TM_AUTH", user: null }, PARENT_ORIGIN);
        } catch {
          // silencioso
        }
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return null;
}
